/**
 * EVERY "GUIDE" LINK IN THE APP, IN ONE MAP.
 *
 * Somebody setting a thing up wants the step-by-step beside the screen they are
 * on, not a search box on another site. So every page header, every settings
 * page and every Integrations drawer carries a small "Guide" link, and every one
 * of them resolves here. When the knowledge base moves an article, this file
 * (and, for connectors, the `docs` field in lib/integrations-catalog) is the only
 * place that changes. A DB-free test (lib/__tests__/guides.test.js) checks every
 * path below is a real article in docs-site and every #anchor a real heading.
 *
 * Keys:
 *   '/route'           a page, matched on the longest route prefix, so a record
 *                      page (/leads/123) inherits its list's guide
 *   'settings:<page>'  a settings page (lib/settings-tabs SETTINGS_TABS value)
 *   'settings:<page>:<section>'  one section of it
 *   'whatsapp:<section>'  one section of the WhatsApp page (/integrations/whatsapp)
 *   'shopify:<section>'   one section of the Shopify page (/integrations/shopify)
 * Connectors are not listed here: their path is the `docs` field of their
 * catalogue entry, read through guideForIntegration().
 *
 * A value is a path under KB_BASE, or { store, leadgen } when the two kinds of
 * workspace are best served by different articles.
 *
 * Pure data and functions - no JSX - so jest can load it.
 */

export const KB_BASE = 'https://knowledgebase.cookiebees.io';

export const GUIDES = {
  // ── Pages ─────────────────────────────────────────────────────────────────
  // A lead business's Dashboard has no article of its own yet; the pipeline
  // guide is where its numbers come from.
  '/dashboard': { store: '/commerce/dashboard/', leadgen: '/leads/pipeline-and-scoring/' },
  // The guided setup, and the first-run page it replaced.
  '/setup': '/getting-started/set-up-in-ten-minutes/',
  '/start': '/getting-started/set-up-in-ten-minutes/',

  '/growth/guide': '/reports/where-to-look/',
  '/growth/actions': '/reports/todays-actions-and-findings/#todays-actions',
  '/growth/findings': '/reports/todays-actions-and-findings/#what-we-found',
  '/growth/customer-value': '/reports/customer-value/',
  '/growth/buyers': '/reports/customer-value/#who-bought',
  '/growth/campaigns': '/reports/campaigns/',
  '/growth/leaks': '/reports/where-you-lose-money/',
  '/growth/channel-roles': '/reports/channel-roles-and-funnels/#channel-roles',
  '/growth/funnels': '/reports/channel-roles-and-funnels/#funnels',
  '/growth/creatives': '/reports/campaigns/#creatives',
  '/growth/audience-builder': '/signals/audiences/#audiences-from-behaviour-stores',

  '/studio': '/signals/signal-catalogue/',
  '/tracking/stream': '/signals/event-stream/',
  // Retired 29 Sep 2026 (redirects to Integrations); kept so old ui-path ids resolve.
  '/tracking/destinations': '/signals/signal-loop/',
  '/signal-health': '/signals/event-stream/#signal-health-stores',
  '/sale-season': '/signals/sale-season/',
  '/number-check': '/signals/number-check/',
  '/value-report': '/signals/what-cookiebees-added/',
  '/tracking/health': '/signals/event-stream/#alerts--diagnostics',
  '/tracking/conversions': '/signals/event-stream/#conversions',
  '/tracking/repeat-clicks': '/reports/repeat-ad-clicks/',
  '/server-container': '/tracking/server-gtm-container/',

  '/commerce': '/commerce/orders-and-customers/#the-orders-list',
  '/commerce/customers': '/commerce/orders-and-customers/#the-customers-list',
  '/commerce/abandoned': '/recovery/abandoned-checkouts/',
  '/commerce/recovery': '/recovery/playbooks/',
  '/commerce/recovery/email': '/email/setup-and-sequences/#edit-a-store-email',
  '/commerce/recommendations': '/commerce/recommendations/',
  '/commerce/recommendations/rules': '/commerce/recommendations/#recommendation-rules',
  '/commerce/costs': '/commerce/costs/',
  '/commerce/attribution': '/reports/attribution/',
  '/commerce/insights': '/commerce/analytics/',
  '/commerce/segments': '/commerce/segments/',
  '/commerce/audiences': '/commerce/audience-lists/',

  '/leads': '/leads/pipeline-and-scoring/#work-the-board',
  '/leads/archive': '/leads/pipeline-and-scoring/#the-archive',
  '/pipelines': '/leads/pipeline-and-scoring/#set-up-your-pipelines',
  '/deals': '/leads/deals/',
  '/offerings': '/leads/deals/#products--services',
  '/contacts': '/leads/contacts-and-cold-captures/',
  '/calling-lists': '/leads/pipeline-and-scoring/#calling-lists',
  '/cold-captures': '/leads/contacts-and-cold-captures/#cold-captures',
  '/follow-ups': '/leads/tasks-and-follow-ups/#follow-ups',
  '/tasks': '/leads/tasks-and-follow-ups/',
  '/calendar': '/meetings/booking/#set-your-availability',
  '/meetings': '/meetings/booking/#the-meetings-page',
  '/calls': '/meetings/call-tracking/#the-calls-page',

  '/tracking/attribution': '/reports/lead-attribution/',
  // The new reports' explorers (lib/report-index), each on the article of the
  // page its numbers came from, tab by tab where the tab has its own.
  '/reports/marketing': '/reports/campaigns/',
  '/reports/marketing/creative': '/reports/campaigns/#creatives',
  '/reports/channels': '/reports/attribution/',
  '/reports/channels/roles': '/reports/channel-roles-and-funnels/#channel-roles',
  '/reports/channels/funnel': '/reports/channel-roles-and-funnels/#funnels',
  '/reports/products': '/commerce/analytics/',
  '/reports/customers': '/reports/customer-value/',
  '/reports/customers/buyers': '/reports/customer-value/#who-bought',
  '/reports/customers/segments': '/commerce/segments/',
  '/reports/leads': '/reports/lead-attribution/',
  '/reports/found': '/reports/todays-actions-and-findings/#what-we-found',
  '/reports/found/leaks': '/reports/where-you-lose-money/',
  '/tracking/email': '/reports/email-analytics/',
  '/tracking/site-scan': '/troubleshooting/common-issues/#where-to-look-first',
  '/tracking/webhooks': '/account/api-and-webhooks/#outbound-webhooks',
  '/tracking/links': '/reports/links/',

  '/whatsapp': '/whatsapp/inbox-and-broadcasts/#the-whatsapp-inbox',
  '/whatsapp/broadcasts': '/whatsapp/inbox-and-broadcasts/#broadcasts',
  '/automation/whatsapp-templates': '/whatsapp/templates/',
  '/automation/whatsapp-flows': '/whatsapp/flows-and-inbox/',
  '/automation/workflow-emails': '/email/setup-and-sequences/#edit-a-workflow-email',
  '/automation/email-templates': '/email/setup-and-sequences/#edit-a-system-email',
  '/automation/sequences': '/automations/sequences/',
  '/automations': '/automations/workflows/',
  '/automations/guide': '/automations/triggers-and-actions/',
  '/scoring': '/leads/pipeline-and-scoring/#lead-scoring',
  '/forms': '/leads/forms/',
  '/popups': '/leads/popups/',
  '/chat': '/leads/website-chat/',

  '/integrations': '/getting-started/integrations/',
  '/settings': '/account/billing-and-team/',
  '/agency': '/agency/console/',

  // ── Settings pages ────────────────────────────────────────────────────────
  'settings:general': '/account/workspace-settings/',
  'settings:team': '/account/team-and-access/',
  'settings:billing': '/account/billing/',
  'settings:account': '/account/your-account/',
  'settings:privacy': '/account/privacy-and-consent/',
  '/integrations/whatsapp': '/whatsapp/setup/',
  '/integrations/shopify': '/getting-started/connect-shopify/',
  'shopify:connection': '/getting-started/connect-shopify/',
  'shopify:catchup': '/getting-started/connect-shopify/#order-catch-up',
  'shopify:pixel': '/getting-started/install-tracking/#shopify',
  'shopify:checkout': '/getting-started/install-tracking/#checkout-apps-and-wallets',
  'shopify:health': '/getting-started/install-tracking/#checkout-apps-and-wallets',
  'whatsapp:connection': '/whatsapp/setup/',
  'whatsapp:templates': '/whatsapp/templates/',
  'whatsapp:replies': '/whatsapp/inbox-and-broadcasts/#automatic-replies',
  'whatsapp:catalog': '/whatsapp/setup/#connect-your-product-catalog',
  'whatsapp:widget': '/whatsapp/website-widget/',
  'whatsapp:usage': '/whatsapp/inbox-and-broadcasts/#usage-and-cost',
  'settings:routing': '/leads/lead-routing/',
  'settings:tracking': '/getting-started/install-tracking/',
  // The page's nine steps, in its order (pages/settings/tracking-tab STEPS).
  'settings:tracking:goal': '/getting-started/install-tracking/#step-1-your-goal',
  'settings:tracking:sites': '/getting-started/install-tracking/#step-2-your-store',
  'settings:tracking:orders': '/getting-started/install-tracking/#step-3-how-orders-reach-us',
  'settings:tracking:serving': '/tracking/first-party-and-params/#serve-tracking-from-your-own-domain',
  'settings:tracking:connect': '/getting-started/install-tracking/#step-5-connect-it',
  'settings:tracking:install': '/getting-started/install-tracking/#step-6-install-the-code',
  'settings:tracking:cookies': '/tracking/first-party-and-params/#keep-the-ad-platforms-cookies-alive',
  'settings:tracking:tagging': '/getting-started/install-tracking/#step-8-tagging-convention',
  'settings:tracking:destinations': '/getting-started/install-tracking/#step-9-where-signals-go',
  'settings:store': '/getting-started/connect-shopify/',
  // The recent-purchases question on a destination's page and in Setup
  // (components/destinations/purchase-backfill-card).
  'signals:recent-purchases': '/signals/signal-loop/#sending-your-recent-purchases-when-you-connect',
};

// Pages in the sidebar with no article yet. Listed rather than silently
// missing, so the knowledge base has a to-do list (the test prints it). The
// Academy pages (Students, Cohorts, Finance, Expenses) are all that is left.
export const NO_GUIDE_YET = ['/students', '/cohorts', '/finance', '/expenses'];

const pick = (v, kind) => (v && typeof v === 'object' ? (v[kind] || v.store || v.leadgen) : v) || null;
const full = (p) => (p ? `${KB_BASE}${p}` : null);

/** The guide path for a key ('settings:...', or an exact route). */
export function guidePath(key, kind = 'store') {
  return pick(GUIDES[key], kind);
}

/** Absolute URL for a key, or null. */
export function guideUrl(key, kind = 'store') {
  return full(guidePath(key, kind));
}

/**
 * The guide for a pathname: the longest mapped route that is the path itself or
 * a parent of it on a segment boundary. /commerce/orders/123 -> /commerce.
 */
export function guideForPath(pathname, kind = 'store') {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  let best = null;
  for (const key of Object.keys(GUIDES)) {
    if (!key.startsWith('/')) continue;
    if ((p === key || p.startsWith(`${key}/`)) && (!best || key.length > best.length)) best = key;
  }
  return best ? full(pick(GUIDES[best], kind)) : null;
}

/** A connector's guide: its catalogue entry's docs path. */
export function guideForIntegration(entry) {
  return entry?.docs ? full(entry.docs) : null;
}
